import React from 'react';
import * as mobx from 'mobx';
import { FormattedMessage, IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { EventStatus, IEventDetailsDTO } from '../../../../../../api/events.dao';
import {
    IScheduledEmailDAO,
    IScheduledEmailDTO,
    IScheduledEmailsListDTO,
    ITrackingEmailValues,
    ScheduledTypes
} from '../../../../../../api/scheduled-email.dao';
import { IAlertsService } from '../../../../../../infrastructure/alerts';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../../../infrastructure/sub-windows';
import { Space } from '../../../../../../constants/html-character-entities';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { cancelText } from '../../../../../../constants/common-labels.constants';
import { TObject } from '../../../../../../infrastructure/type-extensions';
import {
    ARE_YOU_SURE_TEXT,
    REMOVE_EMAIL_TEXT,
    REMOVE_THIS_EMAIL_TEXT,
    REMOVING_SCHEDULED_EMAILS_API_ERROR,
} from './scheduled-emails-form.texts';
import { TRACKING_EVENTS } from '../../../../../../tracking-service/tracking-values';
import { getDateTimeStringForMixPanel } from '../../../../../../infrastructure/utils/date.util';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import { compareAsc } from 'date-fns';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { NOT_AVAILABLE_STRING } from '../../../../../../constants/common.constants';

class ScheduledEmailsFormBaseVM {
    public static scheduledTypes: ScheduledTypes[] = [ScheduledTypes.Event, ScheduledTypes.Meeting];
    @mobx.observable
    public isLoading: boolean;
    @mobx.observable
    public event: IEventDetailsDTO;
    @mobx.observable
    protected emailIdToEditPopupMap: TObject<ISubWindowFacade> = {};
    @mobx.observable
    private emailsList: IScheduledEmailsListDTO;

    constructor(
        protected accountId: number,
        protected _event: IEventDetailsDTO,
        protected _emailsList: IScheduledEmailsListDTO,
        _emailIdToEditPopupMap: TObject<ISubWindowFacade>,
        @IoC.Inject protected readonly subWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject protected readonly scheduledEmailDAO: IScheduledEmailDAO = IoC.Container.get(IScheduledEmailDAO),
        @IoC.Inject protected readonly errorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject protected readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject protected readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject protected readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject protected readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    ) {
        this.emailIdToEditPopupMap = _emailIdToEditPopupMap || {};
        this.event = _event;
        this.emailsList = _emailsList || [];
        mobx.reaction(() => this.globalStorage.getEvent(), () => {
            this.event = this.globalStorage.getEvent();
        });
    }

    public get emails(): IScheduledEmailsListDTO {
        return this.emailsList;
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public setEmailIdToEditPopupMap(id: string, facade: ISubWindowFacade): void {
        this.emailIdToEditPopupMap[id] = facade;
    }

    public getEvent = (): IEventDetailsDTO => {
        return this.event;
    };

    public isEventCancelled(): boolean {
        return this.globalStorage.getEvent().eventStatus === EventStatus.CANCELLED;
    }

    public removeScheduledEmail = (id: string, type: ScheduledTypes): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage {...ARE_YOU_SURE_TEXT} />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage {...REMOVE_THIS_EMAIL_TEXT} />
                    </span>
                    ?
                </span>
            ),
            confirmButtonProps: {
                text: (
                    <FormattedMessage {...REMOVE_EMAIL_TEXT} />
                ),
                modifier: 'confirm-warn',
                handler: () => this.removeScheduledEmailApiCall(id, type)
            },
            cancelButtonProps: {
                text: cancelText(),
                modifier: 'cancel'
            }
        });
    };

    @mobx.action
    public removeScheduledEmailApiCall = async (id: string, type: ScheduledTypes): Promise<void> => {
        try {
            await this.scheduledEmailDAO.deleteScheduledEmail({
                accountId: this.accountId,
                eventId: this.event.eventId,
                id: id,
                type: type
            });
            const removedEmail = this.emails.find(el => el.id === id);
            this.trackingService.get().track(
                TRACKING_EVENTS.REMOVE_SCHEDULE_EMAIL,
                this.emailTrackingData(removedEmail.dateTime, removedEmail.meetingId)
            );
            mobx.runInAction(() => {
                this.emailsList = this.emailsList.filter((el: IScheduledEmailDTO) => el.id !== id);
            });

            this.emailIdToEditPopupMap[id]?.close();
        } catch (error) {
            const errorMessage = this.errorParsing.extractMessage(error);
            alertServerError(REMOVING_SCHEDULED_EMAILS_API_ERROR, errorMessage);
        }
    };

    public isEmailEditFormShown = (id: string): boolean => {
        return this.emailIdToEditPopupMap[id]
            && this.emailIdToEditPopupMap[id].getStatus() !== SubWindowStatus.Closed;
    };

    public getSortedEmails = (): IScheduledEmailDTO[] => {
        return this.emails
            .slice()
            .sort((emailLeft: IScheduledEmailDTO, emailRight: IScheduledEmailDTO) => {
                return compareAsc(new Date(emailLeft.dateTime), new Date(emailRight.dateTime));
            });
    };

    @mobx.action.bound
    public showScheduledEmailForm(id?: string): void {
        const existingSubWindowForThisEmail = this.emailIdToEditPopupMap[id];
        let shallOpenNewSubWindow = true;

        if (id && existingSubWindowForThisEmail && existingSubWindowForThisEmail.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindowForThisEmail.maximize();
            shallOpenNewSubWindow = false;
        }

        if (shallOpenNewSubWindow) {
            this.openNewSubWindow(id);
        }
    };

    // Eslint disable as this needed to override by the child classes
    // eslint-disable-next-line
    protected openNewSubWindow(id: string): void { };

    @mobx.action.bound
    protected onSaveCompleted(data: IScheduledEmailDTO): void {
        this.emails.push(data);
        this.trackingService.get().track(
            TRACKING_EVENTS.ADD_NEW_SCHEDULE_EMAIL,
            this.emailTrackingData(data.dateTime, data.meetingId)
        );
    };

    @mobx.action.bound
    protected onUpdateCompleted(data: IScheduledEmailDTO): void {
        const index = this.emails.findIndex(el => el.id === data.id);
        this.emails.splice(index, 1, data);
        this.trackingService.get().track(
            TRACKING_EVENTS.EDIT_SCHEDULE_EMAIL,
            this.emailTrackingData(data.dateTime, data.meetingId)
        );
    };

    @mobx.action.bound
    protected findEmailById(id: string): IScheduledEmailsListDTO | object {
        return this.emails.find(el => el.id === id) || {};
    };

    @mobx.action
    protected emailTrackingData(dateTime: Date, meetingId?: number): ITrackingEmailValues {
        return {
            'Account Name': this.event.accountName,
            'Account ID': this.accountId,
            'Event Name': this.event.name,
            'Event ID': this.event.eventId,
            'Date & Time of the email': getDateTimeStringForMixPanel(dateTime, this.event.timeZone),
            'Email Type': '',
            'Meeting ID': meetingId || NOT_AVAILABLE_STRING,
            'Roles' : NOT_AVAILABLE_STRING
        };
    }
}

export { ScheduledEmailsFormBaseVM };
